import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  PlusCircle, LayoutList, UserCircle, Users, Megaphone, Inbox, History, LogOut, Image as ImageIcon,
  Video, FileText, Music, Flower2, Trash2, Pin, Loader2, X, Settings,
} from "lucide-react";
import API, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { POST_TYPES, SITE, bnNum } from "@/constants/site";
import { MembersTab, SponsorsTab, InboxTab, ActivityTab } from "./DashboardAdmin";

const TYPE_ICON = { image: ImageIcon, video: Video, megaphone: Megaphone, calendar: Megaphone, file: FileText, music: Music, flower: Flower2 };

function Composer({ onDone }) {
  const [type, setType] = useState("announcement");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [year, setYear] = useState(2026);
  const [eventDate, setEventDate] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const pickFiles = (e) => {
    setFiles(Array.from(e.target.files || []).slice(0, 30));
  };

  const removeFile = (idx) => setFiles((f) => f.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const media = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await API.post("/upload", fd);
        media.push({ kind: data.kind, url: data.url, name: f.name, content_type: data.content_type });
      }
      await API.post("/posts", {
        title,
        type,
        body,
        year: type === "photo" ? Number(year) || 2026 : null,
        event_date: ["event", "cultural"].includes(type) ? eventDate || null : null,
        video_url: type === "video" ? videoUrl || null : null,
        media,
      });
      toast.success("পোস্ট প্রকাশিত হয়েছে");
      setTitle(""); setBody(""); setFiles([]); setVideoUrl(""); setEventDate("");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-xl bg-[#2B0F15] border border-[#F2B134]/20 px-4 py-3 text-[#FDF6EA] placeholder:text-[#D1BBA7]/50 outline-none focus:border-[#F2B134]/70 transition-colors";

  return (
    <form onSubmit={submit} data-testid="composer-form" className="rounded-3xl border border-[#F2B134]/20 bg-[#1E0A0E] p-6 sm:p-8 max-w-3xl">
      <h3 className="font-bengali font-bold text-xl flex items-center gap-3">
        <PlusCircle size={18} className="text-[#F2B134]" /> নতুন পোস্ট
      </h3>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[#F2B134]/80 font-semibold">ধরন</label>
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="composer-type-select" className={`${inputCls} mt-2`}>
            {Object.entries(POST_TYPES).map(([k, v]) => (
              <option key={k} value={k} className="bg-[#1E0A0E]">{v.label}</option>
            ))}
          </select>
        </div>
        {type === "photo" && (
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#F2B134]/80 font-semibold">বছর (অ্যালবাম)</label>
            <input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="composer-year-input" className={`${inputCls} mt-2`} />
          </div>
        )}
        {["event", "cultural"].includes(type) && (
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#F2B134]/80 font-semibold">অনুষ্ঠানের তারিখ</label>
            <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} data-testid="composer-date-input" className={`${inputCls} mt-2`} />
          </div>
        )}
        {type === "video" && (
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#F2B134]/80 font-semibold">ভিডিও লিংক (ঐচ্ছিক)</label>
            <input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="YouTube লিংক" data-testid="composer-video-url-input" className={`${inputCls} mt-2`} />
          </div>
        )}
      </div>

      <div className="mt-4">
        <label className="text-xs uppercase tracking-[0.2em] text-[#F2B134]/80 font-semibold">শিরোনাম</label>
        <input required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="composer-title-input" className={`${inputCls} mt-2 font-bengali`} placeholder="পোস্টের শিরোনাম" />
      </div>

      <div className="mt-4">
        <label className="text-xs uppercase tracking-[0.2em] text-[#F2B134]/80 font-semibold">বিবরণ</label>
        <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} data-testid="composer-body-input" className={`${inputCls} mt-2 resize-none font-bengali`} placeholder="বিস্তারিত লিখুন..." />
      </div>

      <div className="mt-4">
        <label className="text-xs uppercase tracking-[0.2em] text-[#F2B134]/80 font-semibold">
          ছবি / ভিডিও / পিডিএফ (একসঙ্গে একাধিক)
        </label>
        <input
          type="file"
          multiple
          accept="image/*,video/*,application/pdf"
          onChange={pickFiles}
          data-testid="composer-file-input"
          className="mt-2 w-full text-sm text-[#D1BBA7] file:mr-4 file:rounded-full file:border-0 file:bg-[#F2B134] file:px-4 file:py-2 file:text-xs file:font-bold file:text-[#2A0A0E] file:cursor-pointer"
        />
        {files.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-3">
            {files.map((f, i) => (
              <div key={i} className="relative h-16 w-16 rounded-lg overflow-hidden border border-[#F2B134]/30 bg-[#2B0F15] grid place-items-center">
                {f.type.startsWith("image/") ? (
                  <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />
                ) : (
                  <FileText size={20} className="text-[#F2B134]" />
                )}
                <button type="button" onClick={() => removeFile(i)} className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-[#D7261E] grid place-items-center" data-testid={`composer-remove-file-${i}`}>
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <button type="submit" disabled={busy} data-testid="composer-submit-btn" className="btn-gold mt-8 rounded-full px-8 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-50">
        {busy ? <><Loader2 size={16} className="animate-spin" /> প্রকাশ হচ্ছে...</> : "প্রকাশ করুন"}
      </button>
    </form>
  );
}

function PostsList() {
  const [posts, setPosts] = useState([]);
  const { user } = useAuth();

  const load = () => API.get("/posts", { params: { limit: 100 } }).then(({ data }) => setPosts(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (p) => {
    try {
      await API.delete(`/posts/${p.id}`);
      toast.success("পোস্ট মুছে ফেলা হয়েছে");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const pin = async (p) => {
    try {
      await API.patch(`/posts/${p.id}`, { pinned: !p.pinned });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="dashboard-posts-list">
      {posts.length === 0 && <p className="font-bengali text-[#D1BBA7]/70">কোনও পোস্ট নেই — প্রথম পোস্টটি করুন।</p>}
      {posts.map((p) => {
        const Icon = TYPE_ICON[POST_TYPES[p.type]?.icon] || Megaphone;
        return (
          <div key={p.id} className="flex items-start gap-4 rounded-2xl border border-[#F2B134]/18 bg-[#1E0A0E] p-4" data-testid={`dashboard-post-${p.id}`}>
            <span className="h-10 w-10 shrink-0 rounded-xl bg-[#2B0F15] border border-[#F2B134]/25 grid place-items-center text-[#F2B134]">
              <Icon size={16} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-bengali font-bold truncate">{p.title}</p>
              <p className="text-xs text-[#D1BBA7]/70 mt-0.5">
                {POST_TYPES[p.type]?.label} · {p.created_by || "—"} · {p.media?.length ? `${bnNum(p.media.length)}টি ফাইল` : "কোনও ফাইল নেই"}
              </p>
            </div>
            <button onClick={() => pin(p)} data-testid={`dashboard-pin-${p.id}`} className="h-9 w-9 rounded-full border border-[#F2B134]/25 grid place-items-center text-[#D1BBA7] hover:text-[#F2B134]" title="পিন টগল">
              <Pin size={14} className={p.pinned ? "fill-[#F2B134] text-[#F2B134]" : ""} />
            </button>
            {(user?.account_role === "admin" || p.created_by_id === user?.id) && (
              <button onClick={() => del(p)} data-testid={`dashboard-delete-${p.id}`} className="h-9 w-9 rounded-full border border-[#D7261E]/40 grid place-items-center text-[#FFB4AE] hover:bg-[#D7261E]/20" title="মুছুন">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ProfileTab() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState({ current: "", next: "" });
  const [busy, setBusy] = useState(false);

  const change = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await API.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("পাসওয়ার্ড বদলানো হয়েছে");
      setPw({ current: "", next: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-xl bg-[#2B0F15] border border-[#F2B134]/20 px-4 py-3 text-[#FDF6EA] placeholder:text-[#D1BBA7]/50 outline-none focus:border-[#F2B134]/70 transition-colors";

  return (
    <div className="max-w-xl space-y-6" data-testid="profile-tab">
      <div className="rounded-3xl border border-[#F2B134]/20 bg-[#1E0A0E] p-6">
        <div className="flex items-center gap-4">
          <span className="h-14 w-14 rounded-full grid place-items-center font-bold text-[#2A0A0E] bg-[linear-gradient(135deg,#FFE9A8,#F2B134_55%,#D99A1F)]">
            {user?.name?.slice(0, 1)}
          </span>
          <div>
            <p className="font-bengali font-bold text-lg">{user?.name}</p>
            <p className="text-xs text-[#D1BBA7]/80">{user?.role_label} · {user?.account_role === "admin" ? "অ্যাডমিন" : "সদস্য"}</p>
            {user?.phone && <p className="text-xs text-[#D1BBA7]/70 tabular-nums">+91 {user?.phone}</p>}
          </div>
        </div>
        <button onClick={async () => { await logout(); navigate("/"); }} data-testid="profile-logout-btn" className="mt-6 inline-flex items-center gap-2 rounded-full btn-crimson px-5 py-2.5 text-sm font-bold">
          <LogOut size={14} /> লগআউট
        </button>
      </div>

      <form onSubmit={change} className="rounded-3xl border border-[#F2B134]/20 bg-[#1E0A0E] p-6" data-testid="change-password-form">
        <p className="font-bengali font-bold">পাসওয়ার্ড বদলান</p>
        <input required type="password" placeholder="বর্তমান পাসওয়ার্ড" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="profile-current-password-input" className={`${inputCls} mt-4`} />
        <input required type="password" minLength={6} placeholder="নতুন পাসওয়ার্ড (কমপক্ষে ৬ অক্ষর)" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="profile-new-password-input" className={`${inputCls} mt-3`} />
        <button type="submit" disabled={busy} data-testid="profile-change-password-btn" className="btn-gold mt-5 rounded-full px-6 py-2.5 text-sm font-bold disabled:opacity-50">
          {busy ? "হচ্ছে..." : "বদলান"}
        </button>
      </form>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API.get("/settings/site")
      .then(({ data }) => setS({ ...SITE, ...(data || {}) }))
      .catch(() => setS({ ...SITE }));
  }, []);

  const inputCls = "w-full rounded-xl bg-[#2B0F15] border border-[#F2B134]/20 px-4 py-2.5 text-sm text-[#FDF6EA] placeholder:text-[#D1BBA7]/50 outline-none focus:border-[#F2B134]/70 transition-colors";

  if (!s) return <Loader2 className="animate-spin text-[#F2B134]" size={24} />;

  const setField = (k, v) => setS({ ...s, [k]: v });
  const setArr = (k, i, v) => {
    const a = [...(s[k] || ["", ""])];
    a[i] = v;
    setS({ ...s, [k]: a });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await API.put("/admin/settings/site", { data: s });
      toast.success("সেটিংস সংরক্ষিত — সাইট রিফ্রেশ করলে নতুন তথ্য দেখা যাবে");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-3xl rounded-3xl border border-[#F2B134]/20 bg-[#1E0A0E] p-6" data-testid="settings-tab">
      <p className="font-bengali font-bold">সাইট সেটিংস</p>
      <p className="text-xs text-[#D1BBA7]/70 mt-1 font-bengali">ওয়েবসাইটের যোগাযোগ ও সামাজিক তথ্য এখান থেকে বদলানো যায় — দানের ইউপিআই থাকে স্পনসর ট্যাবে।</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        <input value={s.phones?.[0] || ""} onChange={(e) => setArr("phones", 0, e.target.value)} placeholder="ফোন ১" data-testid="settings-phone1-input" className={inputCls} />
        <input value={s.phones?.[1] || ""} onChange={(e) => setArr("phones", 1, e.target.value)} placeholder="ফোন ২" data-testid="settings-phone2-input" className={inputCls} />
        <input value={s.whatsapp?.[0] || ""} onChange={(e) => setArr("whatsapp", 0, e.target.value)} placeholder="হোয়াটসঅ্যাপ ১" data-testid="settings-whatsapp1-input" className={inputCls} />
        <input value={s.whatsapp?.[1] || ""} onChange={(e) => setArr("whatsapp", 1, e.target.value)} placeholder="হোয়াটসঅ্যাপ ২" data-testid="settings-whatsapp2-input" className={inputCls} />
        <input value={s.email || ""} onChange={(e) => setField("email", e.target.value)} placeholder="ইমেইল" data-testid="settings-email-input" className={`${inputCls} sm:col-span-2`} />
        <input value={s.facebook || ""} onChange={(e) => setField("facebook", e.target.value)} placeholder="ফেসবুক লিংক" data-testid="settings-facebook-input" className={inputCls} />
        <input value={s.instagram || ""} onChange={(e) => setField("instagram", e.target.value)} placeholder="ইনস্টাগ্রাম লিংক" data-testid="settings-instagram-input" className={inputCls} />
        <input value={s.addressBN || ""} onChange={(e) => setField("addressBN", e.target.value)} placeholder="ঠিকানা (বাংলায়)" data-testid="settings-address-input" className={`${inputCls} font-bengali`} />
        <input value={s.landmark || ""} onChange={(e) => setField("landmark", e.target.value)} placeholder="ল্যান্ডমার্ক" data-testid="settings-landmark-input" className={inputCls} />
      </div>
      <button type="submit" disabled={busy} data-testid="settings-save-btn" className="btn-gold mt-6 rounded-full px-6 py-2.5 text-sm font-bold disabled:opacity-50">
        {busy ? "সংরক্ষণ হচ্ছে..." : "সংরক্ষণ করুন"}
      </button>
    </form>
  );
}

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("composer");

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#120507]">
        <Loader2 className="animate-spin text-[#F2B134]" size={32} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const isAdmin = user.account_role === "admin";
  const tabs = [
    { id: "composer", label: "নতুন পোস্ট", icon: PlusCircle },
    { id: "posts", label: "পোস্টসমূহ", icon: LayoutList },
    { id: "profile", label: "প্রোফাইল", icon: UserCircle },
    ...(isAdmin
      ? [
          { id: "members", label: "সদস্য", icon: Users, admin: true },
          { id: "sponsors", label: "স্পনসর", icon: Megaphone, admin: true },
          { id: "settings", label: "সেটিংস", icon: Settings, admin: true },
          { id: "inbox", label: "ইনবক্স", icon: Inbox, admin: true },
          { id: "activity", label: "অ্যাক্টিভিটি", icon: History, admin: true },
        ]
      : []),
  ];

  return (
    <div className="min-h-screen bg-[#120507] flex">
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-[#F2B134]/15 p-5 sticky top-0 h-screen">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl overflow-hidden bg-[#FDF6EA] border border-[#F2B134]/40">
            <img src="/logo.jpg" alt="" className="w-full h-full object-cover" />
          </span>
          <div>
            <p className="font-bengali font-bold text-sm">ড্যাশবোর্ড</p>
            <p className="text-[10px] text-[#D1BBA7]/70">রাজেন্দ্রলাল স্ট্রিট</p>
          </div>
        </div>
        <nav className="mt-8 flex-1 space-y-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`dashboard-tab-${t.id}`}
              className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                tab === t.id ? "bg-[#F2B134]/15 text-[#F2B134] border border-[#F2B134]/30" : "text-[#D1BBA7] hover:bg-[#F2B134]/8"
              }`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </nav>
        <button onClick={async () => { await logout(); navigate("/"); }} data-testid="dashboard-logout-btn" className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm text-[#FFB4AE] hover:bg-[#D7261E]/15">
          <LogOut size={16} /> লগআউট
        </button>
      </aside>

      <div className="flex-1 min-w-0">
        <div className="md:hidden sticky top-0 z-40 glass-nav border-b border-[#F2B134]/15 px-4 py-3 flex gap-2 overflow-x-auto no-scrollbar">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`dashboard-tab-mobile-${t.id}`}
              className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold ${tab === t.id ? "btn-gold" : "border border-[#F2B134]/25 text-[#D1BBA7]"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <main className="p-5 sm:p-8">
          {tab === "composer" && <Composer />}
          {tab === "posts" && <PostsList />}
          {tab === "profile" && <ProfileTab />}
          {tab === "members" && isAdmin && <MembersTab />}
          {tab === "sponsors" && isAdmin && <SponsorsTab />}
          {tab === "settings" && isAdmin && <SettingsTab />}
          {tab === "inbox" && isAdmin && <InboxTab />}
          {tab === "activity" && isAdmin && <ActivityTab />}
        </main>
      </div>
    </div>
  );
}
